import React, { useEffect, useState, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Popper,
  Fade,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import BagSvg from "./images/NewBagIcon";
import UserSvg from "./images/NewUserIcon";
import SearchIcon from "./images/NewSearchIcon";
import { IMG_DAINASOUL_SMALL_LOGO_NEW as DainasoulLogo } from "./imageConfig";
import { useCheckout } from "./CheckoutContext";
import { useDrawer } from "./DrawerContext";
import { useAuth } from "./contexts/AuthContext";
import ShopDropdown from "./ShopDropdown";
import SearchDrawer from "./SearchDrawer";
import NavDrawer from "./NavDrawer";
import MenuIconSvg from "./images/MenuIconSvg";

const Header = ({ hoodieRef, userCountry }) => {
  const theme = useTheme();
  // Using 'md' as breakpoint for mobile/tablet vs laptop since the design is complex
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const isLaptop = useMediaQuery(theme.breakpoints.up("md"));

  const { openDrawer } = useDrawer();
  const isCheckoutPage = location.pathname.startsWith("/checkout");
  const { user } = useAuth();
  const [openSearch, setOpenSearch] = useState(false);

  const { state: checkoutState } = useCheckout();
  const cartItemCount = checkoutState.cartItems.reduce(
    (sum, item) => sum + (item.quantity || 1),
    0
  );

  // Dropdown state
  const [openShop, setOpenShop] = useState(false);
  const [openNav, setOpenNav] = useState(false);
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const anchorRef = useRef(null);
  const timeoutRef = useRef(null); // To handle hover delay

  const handleShopEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpenShop(true);
  };

  const handleShopLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setOpenShop(false);
    }, 100); // Small delay to allow moving mouse to the dropdown
  };

  const scrollToTop = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  // Responsive styles
  const getLogoStyles = () => {
    if (isMobile) {
      return {
        width: "40px",
        marginLeft: "16px",
        marginRight: "16px",
      };
    } else {
      return {
        width: "50px",
        marginLeft: "10px", // Moved near edge
        marginRight: "50px",
      };
    }
  };

  const getTitleStyles = () => {
    if (isMobile) {
      return {
        fontSize: "20px",
      };
    } else {
      return {
        fontSize: "32px",
      };
    }
  };

  const getRightPadding = () => {
    if (isMobile) {
      return 1;
    } else {
      return 0; // Move as close to edge as possible
    }
  };

  // State for scroll behavior
  const [showShippingBar, setShowShippingBar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleScroll = () => {
    if (window.scrollY > lastScrollY) {
      // scrolling down
      setShowShippingBar(false);
    } else {
      // scrolling up
      setShowShippingBar(true);
    }
    setLastScrollY(window.scrollY);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  return (
    <>
      {!isCheckoutPage && (
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#000",
            color: "#fff",
            textAlign: "center",
            fontSize: "14px",
            fontWeight: 500,
            py: "8px",
            position: "fixed",
            top: showShippingBar ? 0 : "-50px",
            transition: "top 0.3s ease",
            zIndex: 1301,
          }}
        >
          Free{" "}
          {typeof userCountry === "object"
            ? userCountry?.name
            : userCountry || "UK"}{" "}
          shipping
        </Box>
      )}
      <AppBar
        position="fixed"
        style={{
          backgroundColor: "#fff",
          height: isMobile ? "60px" : isCheckoutPage ? "75px" : "120px",
          justifyContent: "flex-start",
          top: isCheckoutPage ? "0px" : showShippingBar ? "34px" : "0px",
          transition: "top 0.3s ease",
          boxShadow: "none", // Cleaner look for mega menu
          borderBottom: isMobile ? "none" : "1px solid #e0e0e0",
        }}
      >
        <Toolbar
          sx={{
            height: isMobile ? "60px" : "75px",
            minHeight: isMobile ? "60px" : "75px",
            px: isMobile ? 0.5 : 0.5, // Better breathing room on mobile
            justifyContent: "space-between",
            position: "relative",
          }}
        >
          {/* Mobile View: Left (Menu + Search) */}
          {isMobile && !isCheckoutPage && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={() => setOpenNav(true)}
                sx={{ color: "#000", p: 1 }}
              >
                <MenuIconSvg />
              </IconButton>
              <IconButton
                onClick={() => setOpenSearch(true)}
                sx={{ color: "#000", p: 1 }}
              >
                <SearchIcon size={20} color="#000" />
              </IconButton>
            </Box>
          )}

          {/* Desktop View: Left (Logo) */}
          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                height: '100%',
              }}
            >
              <img
                src={DainasoulLogo}
                alt="Dainasoul Logo"
                style={{
                  ...getLogoStyles(),
                  height: "auto",
                  cursor: "pointer",
                }}
                onClick={scrollToTop}
              />
            </Box>
          )}

          {/* Center - Title (Mobile & Desktop) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1,
              gap: isMobile ? "8px" : "0px",
            }}
          >
            {isMobile && (
              <img
                src={DainasoulLogo}
                alt="Dainasoul Logo"
                style={{
                  width: "35px",
                  height: "auto",
                  cursor: "pointer",
                }}
                onClick={scrollToTop}
              />
            )}
            <Box
              sx={{
                position: "relative",
                display: "inline-block",
                cursor: "pointer",
              }}
              onClick={scrollToTop}
            >
              <Typography
                variant="h4"
                sx={{
                  color: "#000",
                  fontWeight: 600,
                  fontFamily: '"Playfair Display", "Times New Roman", serif',
                  letterSpacing: "0.025rem",
                  fontSize: isMobile ? "24px" : "32px",
                }}
              >
                DAINASOUL
              </Typography>
              <Typography
                sx={{
                  position: "absolute",
                  top: isMobile ? 0 : 2,
                  right: isMobile ? -6 : -8,
                  fontSize: isMobile ? "10px" : "12px",
                  lineHeight: 1,
                  color: "black",
                }}
              >
                ®
              </Typography>
            </Box>
          </Box>

          {/* Right View: Cart + User (Mobile & Desktop) */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              flex: 1,
              pr: isMobile ? 0.5 : "0px",
            }}
          >
            {/* Search (Desktop only here, moved to left on mobile) */}
            {!isMobile && !isCheckoutPage && (
              <IconButton
                color="inherit"
                sx={{
                  mr: 2,
                  mt: "6px",
                  "&:hover": { backgroundColor: "transparent" },
                }}
                onClick={() => setOpenSearch(true)}
              >
                <SearchIcon size={22} color="#000" />
              </IconButton>
            )}

            {!isCheckoutPage && (
              <IconButton
                color="inherit"
                sx={{
                  mr: isMobile ? 0.5 : 2,
                  mt: "2px",
                  "&:hover": { backgroundColor: "transparent" },
                }}
                onClick={() => navigate(user ? "/account" : "/login")}
              >
                <UserSvg size={isMobile ? 20 : 22} color="#000" />
              </IconButton>
            )}

            {!isCheckoutPage && (
              <IconButton
                color="inherit"
                sx={{
                  mr: isMobile ? 0 : 2,
                  mt: "-2px",
                  "&:hover": { backgroundColor: "transparent" },
                }}
                onClick={openDrawer}
              >
                <BagSvg size={isMobile ? 20 : 22} color="#000" count={cartItemCount} />
              </IconButton>
            )}
          </Box>
        </Toolbar>

        <NavDrawer open={openNav} onClose={() => setOpenNav(false)} />

        {/* Shop Dropdown Popper (Desktop only) */}
        {!isMobile && (
          <Popper
            open={openShop}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            transition
            style={{
              zIndex: 1290,
              width: '100%',
              position: 'fixed',
              left: 0,
              top: showShippingBar ? '154px' : '120px',
            }}
            onMouseEnter={handleShopEnter}
            onMouseLeave={handleShopLeave}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Box>
                  <ShopDropdown onClose={() => setOpenShop(false)} />
                </Box>
              </Fade>
            )}
          </Popper>
        )}

        {/* NEW SUB-HEADER inside AppBar (Desktop only) */}
        {!isMobile && !isCheckoutPage && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "32px",
              height: "45px",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Typography
              ref={anchorRef}
              onMouseEnter={handleShopEnter}
              onMouseLeave={handleShopLeave}
              onClick={() => {
                navigate('/all-products');
                setOpenShop(false);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                boxSizing: "border-box",
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "1px",
                cursor: "pointer",
                color: "#000",
                marginBottom: "-1px",
                borderBottom: openShop ? "2px solid #000" : "2px solid transparent",
                "&:hover": { color: "#666" }
              }}
            >
              SHOP
            </Typography>
            <Typography
              onClick={() => navigate('/all-products', { state: { customHeading: "Last Chance" } })}
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
                boxSizing: "border-box",
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "1px",
                color: "#ff4d4d",
                cursor: "pointer",
                marginBottom: "-1px",
              }}
            >
              LAST CHANCE
            </Typography>
          </Box>
        )}
      </AppBar>
      <SearchDrawer open={openSearch} onClose={() => setOpenSearch(false)} />
    </>
  );
};

export default Header;
